import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const listAdditionsSkill: Skill = {
  id: "listAdditions",
  description:
    "Lista las adiciones (extras) configuradas en el restaurante.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando adiciones…",
      successMsg: "✓ Adiciones obtenidas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/additions`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              additions: items.map((a) => ({
                id: a.id,
                name: a.name,
                price: a.price,
                isArchived: a.isArchived,
              })),
            },
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const createAdditionSkill: Skill = {
  id: "createAddition",
  description: "Crea una nueva adición / extra configurable.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      name: { type: "string" },
      price: { type: "number" },
    },
    required: ["restaurantId", "name", "price"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const name = String(args.name ?? "").trim();
    const price = Number(args.price);
    if (!restaurantId || !name || !Number.isFinite(price) || price < 0) {
      return { ok: false, error: "Datos inválidos." };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Crear adición",
      details: [
        { label: "Nombre", value: name },
        { label: "Precio", value: `$${price.toLocaleString()}` },
      ],
      confirmLabel: "Crear",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando adición "${name}"…`,
      successMsg: `✓ "${name}" creada`,
      restaurantId,
      targetTab: "adiciones",
      targetTabLabel: "Adiciones",
      run: async () => {
        try {
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/additions`,
            { name, price },
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { id: data?.id, name, price },
            humanSummary: `Adición "${name}" creada.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const updateAdditionSkill: Skill = {
  id: "updateAddition",
  description: "Actualiza una adición (renombrar o cambiar precio).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      additionId: { type: "string" },
      name: { type: "string" },
      price: { type: "number" },
    },
    required: ["restaurantId", "additionId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const additionId = String(args.additionId ?? "");
    if (!restaurantId || !additionId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.name) { patch.name = String(args.name); details.push({ label: "Nombre", value: patch.name as string }); }
    if (Number.isFinite(Number(args.price))) { patch.price = Number(args.price); details.push({ label: "Precio", value: `$${(patch.price as number).toLocaleString()}` }); }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({ title: "Actualizar adición", details, confirmLabel: "Actualizar" });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando adición…",
      successMsg: "✓ Adición actualizada",
      restaurantId,
      targetTab: "adiciones",
      targetTabLabel: "Adiciones",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/additions/${additionId}`,
            patch,
            ctx.accessToken,
          );
          return { ok: true as const, result: { additionId, ...patch }, humanSummary: "Adición actualizada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const archiveAdditionSkill: Skill = {
  id: "archiveAddition",
  description: "Archiva una adición (oculta sin eliminar).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, additionId: { type: "string" } },
    required: ["restaurantId", "additionId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const additionId = String(args.additionId ?? "");
    if (!restaurantId || !additionId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Archivar adición",
      confirmLabel: "Archivar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Archivando adición…",
      successMsg: "✓ Adición archivada",
      restaurantId,
      targetTab: "adiciones",
      targetTabLabel: "Adiciones",
      run: async () => {
        try {
          await apiWithAuth.post(
            `/restaurants/${restaurantId}/additions/${additionId}/archive`,
            {},
            ctx.accessToken,
          );
          return { ok: true as const, result: { additionId, archived: true }, humanSummary: "Adición archivada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const restoreAdditionSkill: Skill = {
  id: "restoreAddition",
  description: "Restaura una adición archivada.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, additionId: { type: "string" } },
    required: ["restaurantId", "additionId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const additionId = String(args.additionId ?? "");
    if (!restaurantId || !additionId) return { ok: false, error: "Faltan IDs." };
    return withFollow(ctx, {
      intro: "Restaurando adición…",
      successMsg: "✓ Adición restaurada",
      restaurantId,
      targetTab: "adiciones",
      targetTabLabel: "Adiciones",
      run: async () => {
        try {
          await apiWithAuth.post(
            `/restaurants/${restaurantId}/additions/${additionId}/restore`,
            {},
            ctx.accessToken,
          );
          return { ok: true as const, result: { additionId, restored: true }, humanSummary: "Adición restaurada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const setProductAdditionsSkill: Skill = {
  id: "setProductAdditions",
  description:
    "Vincula adiciones a un producto. Reemplaza la lista actual con la nueva.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
      additionIds: {
        type: "array",
        items: { type: "string" },
      },
    },
    required: ["restaurantId", "productId", "additionIds"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    const additionIds = Array.isArray(args.additionIds) ? args.additionIds.map(String) : [];
    if (!restaurantId || !productId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Configurar adiciones del producto",
      details: [{ label: "Adiciones", value: `${additionIds.length} seleccionada(s)` }],
      confirmLabel: "Aplicar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Configurando adiciones…",
      successMsg: "✓ Adiciones configuradas",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          await apiWithAuth.put(
            `/restaurants/${restaurantId}/products/${productId}/additions`,
            { additionIds },
            ctx.accessToken,
          );
          return { ok: true as const, result: { productId, additionIds }, humanSummary: "Adiciones configuradas." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
